import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FolderOpen, Trash2, Code2, Package } from 'lucide-react';
import { useProjectStore } from '../../stores/useProjectStore';
import s from './Pages.module.css';

export function ProjectsPage() {
  const navigate = useNavigate();
  const projects = useProjectStore(p => p.projects);
  const createProject = useProjectStore(p => p.createProject);
  const deleteProject = useProjectStore(p => p.deleteProject);

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    const id = await createProject(name, 'blank');
    setNewName('');
    setShowCreate(false);
    navigate(`/projects/${id}`);
  };

  const handleDelete = async (id: string) => {
    await deleteProject(id);
    setDeletingId(null);
  };

  return (
    <div className={s.page}>
      <div className={s.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 className={s.pageTitle}>Projects</h1>
            <p className={s.pageSubtitle}>
              Manage custom code workspaces and linked prefab deployments.
            </p>
          </div>
          <button className={s.btnPrimary} onClick={() => setShowCreate(true)}>
            <Plus size={16} /> New Project
          </button>
        </div>
      </div>

      {/* Create dialog */}
      {showCreate && (
        <div className={`${s.card} ${s.cardStatic}`} style={{ marginBottom: 'var(--spacing-xl)', maxWidth: 480 }}>
          <h3 style={{ fontSize: 15, marginBottom: 'var(--spacing-md)' }}>Create Project</h3>
          <div className={s.formGroup} style={{ marginBottom: 'var(--spacing-md)' }}>
            <input
              className={s.formInput}
              placeholder="Project name"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              autoFocus
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={s.btnPrimary} onClick={handleCreate} disabled={!newName.trim()}>Create</button>
            <button className={s.btnSecondary} onClick={() => { setShowCreate(false); setNewName(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {projects.length === 0 ? (
        <div className={s.emptyState}>
          <div className={s.emptyIcon}><FolderOpen size={48} /></div>
          <div className={s.emptyTitle}>No projects yet</div>
          <div className={s.emptyDesc}>
            Create a project to start writing custom code, or deploy a prefab to get started quickly.
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className={s.btnPrimary} onClick={() => setShowCreate(true)}>
              <Plus size={16} /> New Project
            </button>
            <button className={s.btnSecondary} onClick={() => navigate('/prefabs')}>
              <Package size={16} /> Browse Prefabs
            </button>
          </div>
        </div>
      ) : (
        <div className={s.cardGrid}>
          {projects.map(proj => (
            <div key={proj.id} className={s.card} onClick={() => navigate(`/projects/${proj.id}`)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className={s.cardTitle}>
                  <FolderOpen size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                  {proj.name}
                </div>
                <button
                  className={s.btnGhost}
                  style={{ padding: 4 }}
                  onClick={e => { e.stopPropagation(); setDeletingId(proj.id); }}
                  title="Delete project"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className={s.cardDesc}>
                {proj.fileCount} file{proj.fileCount !== 1 ? 's' : ''}
              </div>
              <div className={s.cardMeta}>
                <span className={`${s.badge} ${s.badgeNeutral}`}>{proj.source}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                  {new Date(proj.updatedAt).toLocaleDateString()}
                </span>
              </div>

              {/* Delete confirmation */}
              {deletingId === proj.id && (
                <div
                  onClick={e => e.stopPropagation()}
                  style={{
                    marginTop: 'var(--spacing-md)',
                    padding: 'var(--spacing-md)',
                    background: 'rgba(248,81,73,0.08)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-danger)',
                  }}
                >
                  <div style={{ fontSize: 13, marginBottom: 8 }}>Delete "{proj.name}"?</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className={s.btnPrimary} style={{ background: 'var(--color-danger)', fontSize: 12, padding: '4px 12px' }} onClick={() => handleDelete(proj.id)}>
                      Delete
                    </button>
                    <button className={`${s.btnGhost} ${s.btnSmall}`} onClick={() => setDeletingId(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* New project card */}
          <button
            className={s.card}
            style={{ border: '1px dashed var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}
            onClick={() => setShowCreate(true)}
          >
            <div style={{ textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
              <Plus size={24} />
              <div style={{ fontSize: 13, marginTop: 4 }}>New Project</div>
            </div>
          </button>
        </div>
      )}

      {/* IDE link */}
      <div style={{ marginTop: 'var(--spacing-3xl)', textAlign: 'center' }}>
        <button className={s.btnSecondary} onClick={() => navigate('/ide')}>
          <Code2 size={16} /> Open IDE
        </button>
      </div>
    </div>
  );
}
